import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import MenuBook from '@mui/icons-material/MenuBook';
import School from '@mui/icons-material/School';
import Search from '@mui/icons-material/Search';
import SportsEsports from '@mui/icons-material/SportsEsports';
import Today from '@mui/icons-material/Today';
import { useSearchParams } from 'react-router-dom';
import StudentAvatarView from '../../shared/StudentAvatarView';
import { teacherTypography } from '../../design/typography';
import { getStudentActivities, getStudentActivityDetail } from '../api/studentActivities';

const actionFocus = { '&:focus-visible': { outline: '3px solid #1E40AF', outlineOffset: 2 } };
const retryStyle = { minHeight: 44, color: '#1E40AF', ...actionFocus };

const dateLabel = (day) => {
  if (!day) return '-';
  const value = new Date(`${String(day).slice(0, 10)}T12:00:00`);
  return Number.isNaN(value.getTime()) ? '-' : value.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

const detailLabel = (item) => {
  if (item.kind === 'mini_task_daily') return { title: 'Mini Görev', subtitle: `${item.value} tur tamamlandı`, icon: <SportsEsports /> };
  if (item.kind === 'mini_task') return { title: 'Mini Görev tamamlandı', subtitle: item.level ? `Seviye ${item.level} · Tamamlanan tur` : 'Tamamlanan tur', icon: <SportsEsports /> };
  if (item.kind === 'book') return { title: 'Kitap tamamlandı', subtitle: item.detail, icon: <MenuBook /> };
  if (item.kind === 'weekly_reading') return { title: 'Haftalık okuma tamamlandı', subtitle: 'Haftalık Okuma', icon: <MenuBook /> };
  const metric = item.detail?.toLowerCase();
  if (metric === 'questions') return { title: 'Soru çözümü', subtitle: `${item.value} soru`, icon: <School /> };
  if (metric === 'pages') return { title: 'Okuma kaydı', subtitle: `${item.value} sayfa`, icon: <MenuBook /> };
  return { title: 'Çalışma kaydı', subtitle: `${item.value} dakika`, icon: <Today /> };
};

const studentSummary = (student) => {
  const parts = [];
  if (student.mini_tasks) parts.push(`${student.mini_tasks} Mini Görev`);
  if (student.books) parts.push(`${student.books} kitap`);
  if (student.weekly_readings) parts.push(`${student.weekly_readings} haftalık okuma`);
  if (student.study_records) parts.push(`${student.study_records} çalışma kaydı`);
  return parts.join(' · ') || 'Bu dönemde etkinlik kaydı yok';
};

function ActivityHistory({ schoolId, student, window, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [events, setEvents] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [timeZone, setTimeZone] = useState('Europe/Istanbul');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEvents([]);
    setNextOffset(null);
    setError('');
    getStudentActivityDetail(schoolId, student.student_id, {
      class_id: window.class_id, start_date: window.start_date, end_date: window.end_date, limit: 30, offset: 0, summarize_mini_tasks: true,
    }).then((data) => {
      if (!cancelled) { setEvents(data.events || []); setNextOffset(data.next_offset); setTimeZone(data.time_zone || 'Europe/Istanbul'); }
    }).catch(() => { if (!cancelled) setError('Etkinlik geçmişi yüklenemedi.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [schoolId, student.student_id, window.class_id, window.start_date, window.end_date, retry]);

  const loadMore = async () => {
    if (nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const data = await getStudentActivityDetail(schoolId, student.student_id, {
        class_id: window.class_id, start_date: window.start_date, end_date: window.end_date, limit: 30, offset: nextOffset, summarize_mini_tasks: true,
      });
      setEvents((current) => [...current, ...(data.events || [])]);
      setNextOffset(data.next_offset);
    } catch (_) { setError('Diğer hareketler getirilemedi.'); }
    finally { setLoadingMore(false); }
  };

  const name = `${student.first_name} ${student.last_name}`.trim();
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} aria-labelledby="student-history-title">
      <DialogTitle id="student-history-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <StudentAvatarView studentId={student.student_id} avatarId={student.avatar_id} starterEmoji={student.starter_emoji} name={name} size={48} />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ ...teacherTypography.sectionTitle, display: 'block', overflowWrap: 'anywhere' }}>{name}</Typography>
            <Typography variant="body2" color="text.secondary">{dateLabel(window.start_date)} – {dateLabel(window.end_date)} etkinlikleri</Typography>
          </Box>
        </Stack>
        <IconButton aria-label="Kapat" onClick={onClose} sx={{ minWidth: 44, minHeight: 44, ...actionFocus }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip label={`${student.event_count} etkinlik`} />
            <Chip variant="outlined" label={`${student.active_days} etkinlik günü`} />
            <Chip variant="outlined" label={`Son kayıt: ${dateLabel(student.last_active_on)}`} />
          </Stack>
          {error && <Alert severity="error" action={<Button sx={retryStyle} onClick={() => events.length ? loadMore() : setRetry((n) => n + 1)}>Tekrar dene</Button>}>{error}</Alert>}
          {loading ? Array.from({ length: 4 }, (_, index) => <Skeleton key={index} variant="rounded" height={62} />) : null}
          {!loading && !events.length && !error ? <Alert severity="info">Bu dönemde etkinlik kaydı bulunmuyor.</Alert> : null}
          {events.map((event) => {
            const item = detailLabel(event);
            return (
              <Paper key={`${event.kind}-${event.event_id}`} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: 'text.secondary' }}>{item.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{item.subtitle}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">{dateLabel(event.day)}</Typography>
                    {event.occurred_at && <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(event.occurred_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone })}
                    </Typography>}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
          {nextOffset != null && !loading ? <Button disabled={loadingMore} onClick={loadMore} sx={{ minHeight: 44, color: '#1E40AF', ...actionFocus }}>
            {loadingMore ? 'Yükleniyor...' : 'Daha fazla göster'}
          </Button> : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function StudentActivitiesPage({ schoolId }) {
  const [params, setParams] = useSearchParams();
  const classId = params.get('class_id') || '';
  const startDate = params.get('start_date') || '';
  const endDate = params.get('end_date') || '';
  const filter = ['active', 'without'].includes(params.get('filter')) ? params.get('filter') : 'all';
  const studentId = params.get('student_id');
  const expectedCount = params.get('expected_count');
  const isSnapshot = params.get('summary_source') === 'snapshot';
  const [payload, setPayload] = useState(null);
  const selected = payload?.students?.find((student) => String(student.student_id) === studentId);
  const search = params.get('q') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPayload(null);
    setError('');
    if (schoolId) {
      getStudentActivities(schoolId, { class_id: classId, start_date: startDate, end_date: endDate })
        .then((data) => { if (!cancelled) setPayload(data); })
        .catch(() => { if (!cancelled) setError('Öğrenci etkinlikleri yüklenemedi.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [schoolId, classId, startDate, endDate, retry]);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => { if (value) next.set(key, value); else next.delete(key); });
    setParams(next, { replace: Boolean(patch.q !== undefined) });
  };
  const students = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    return (payload?.students || []).filter((student) => {
      if (filter === 'active' && !student.active) return false;
      if (filter === 'without' && student.active) return false;
      return !term || `${student.first_name} ${student.last_name} ${student.student_no}`.toLocaleLowerCase('tr').includes(term);
    });
  }, [payload, filter, search]);

  return (
    <Stack spacing={2.5} sx={{ pb: 3, fontFamily: teacherTypography.pageTitle.fontFamily, fontSynthesis: 'none' }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5}>
          <Box>
            <Typography component="h1" sx={teacherTypography.pageTitle}>Öğrenci Etkinlikleri</Typography>
            <Typography variant="body2" color="text.secondary">Sınıfın etkinlik katılımını ve öğrencilerin kayıt geçmişini inceleyin.</Typography>
          </Box>
          {payload && <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select inputProps={{ 'aria-label': 'Şube' }} sx={{ minHeight: 44 }} value={String(payload.class_id)} onChange={(event) => updateParams({ class_id: event.target.value, student_id: '', expected_count: '', summary_source: '' })}>
              {(payload.available_classes || []).map((row) => <MenuItem key={row.id} value={String(row.id)}>{row.code}</MenuItem>)}
            </Select>
          </FormControl>}
        </Stack>
      </Paper>
      {error && <Alert severity="error" action={<Button sx={retryStyle} onClick={() => setRetry((n) => n + 1)}>Tekrar dene</Button>}>{error}</Alert>}
      {loading && <Paper role="status" aria-label="Öğrenci etkinlikleri yükleniyor" elevation={0} sx={{ p: 2, borderRadius: 3 }}>{Array.from({ length: 7 }, (_, index) => <Skeleton key={index} variant="rounded" height={64} sx={{ mb: 1 }} />)}</Paper>}
      {payload && !loading && (
        <>
          {expectedCount !== null && Number(expectedCount) !== payload.active_students && (
            <Alert severity="info">Ana sayfada {expectedCount}, güncel listede {payload.active_students} etkin öğrenci var. {isSnapshot ? 'Haftalık rapor tarihsel olabilir. ' : ''}Geç eklenen kayıtlar veya sınıf üyeliği değişiklikleri sayıyı değiştirmiş olabilir.</Alert>
          )}
          <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1.5}>
              <Typography color="text.secondary" variant="body2">
                {dateLabel(payload.start_date)} – {dateLabel(payload.end_date)} · {payload.total_students} öğrenci
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {[{ id: 'all', label: `Tümü · ${payload.total_students}` },
                  { id: 'active', label: `Etkinliği var · ${payload.active_students}` },
                  { id: 'without', label: `Kayıt yok · ${payload.without_records}` }].map((option) => (
                  <Chip key={option.id} clickable variant={filter === option.id ? 'filled' : 'outlined'}
                    aria-pressed={filter === option.id}
                    color={filter === option.id ? 'primary' : 'default'} label={option.label}
                    onClick={() => updateParams({ filter: option.id === 'all' ? '' : option.id })}
                    sx={{ minHeight: 44, fontWeight: 600, '&:focus-visible': { outline: '3px solid #1E40AF', outlineOffset: 2 } }} />
                ))}
              </Stack>
              <TextField fullWidth size="small" placeholder="Öğrenci adı veya numarasıyla ara"
                label="Öğrenci ara" value={search} onChange={(event) => updateParams({ q: event.target.value, student_id: '' })}
                sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }} />
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {!students.length ? <Box sx={{ p: 4 }}><Typography color="text.secondary">Bu filtreye uyan öğrenci bulunamadı.</Typography></Box> : (
              students.map((student, index) => {
                const name = `${student.first_name} ${student.last_name}`.trim();
                return (
                  <React.Fragment key={student.student_id}>
                    {index > 0 && <Divider />}
                    <Box component="button" type="button" onClick={() => updateParams({ student_id: String(student.student_id) })}
                      aria-label={`${name} etkinlik geçmişini aç`}
                      sx={{
                        width: '100%', textAlign: 'left', appearance: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, md: 2 },
                        border: 0, backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:focus-visible': { outline: '3px solid #1E40AF', outlineOffset: -3 },
                        minHeight: 78,
                      }}>
                      <StudentAvatarView studentId={student.student_id} avatarId={student.avatar_id}
                        starterEmoji={student.starter_emoji} name={name} size={48} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={teacherTypography.studentName}>{name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{studentSummary(student)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.last_active_on ? `Son etkinlik: ${dateLabel(student.last_active_on)}` : `No: ${student.student_no}`}
                        </Typography>
                      </Box>
                      <ChevronRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
                    </Box>
                  </React.Fragment>
                );
              })
            )}
          </Paper>
        </>
      )}
      {studentId && payload && !selected && <Alert severity="info">Öğrenci seçili sınıfta bulunamadı.</Alert>}
      {selected && payload && <ActivityHistory key={selected.student_id} schoolId={schoolId} student={selected} window={payload} onClose={() => updateParams({ student_id: '' })} />}
    </Stack>
  );
}
